import { useEffect, useState } from "react";
import type {
  BannerCountdown,
  DashboardBanner,
} from "@/content/dashboardBanners";
import { countdownEnd, startCountdown } from "./bannerCountdowns";

export type CountdownView = {
  /** Whole seconds left while the countdown runs; never below zero. */
  seconds: number | null;
  /** What the banner turns into once its countdown has run out. */
  ended: BannerCountdown["ends"] | null;
};

const NONE: CountdownView = { seconds: null, ended: null };

/**
 * The countdown of the banner on screen, ticking each second. Being drawn is
 * the first view, so a countdown from the first view starts here. Run out,
 * a banner set to hide is left out by the runtime on the next render, which
 * this hook's last tick causes.
 */
export const useBannerCountdown = (
  banner: DashboardBanner | null
): CountdownView => {
  const id = banner?.id ?? null;
  const [clock, setClock] = useState(() => ({ id, now: Date.now() }));
  // A placement mounts long before its banner arrives, or opens: the clock
  // is read again as another banner is handed over, never drawn stale.
  let { now } = clock;
  if (clock.id !== id) {
    now = Date.now();
    setClock({ id, now });
  }
  const end = banner ? countdownEnd(banner, now) : null;
  const running = end !== null && end > now;

  useEffect(() => {
    if (banner) startCountdown(banner, Date.now());
  }, [banner]);

  useEffect(() => {
    if (!running) return;
    const tick = setInterval(
      () => setClock((last) => ({ ...last, now: Date.now() })),
      1000
    );
    return () => clearInterval(tick);
  }, [running]);

  if (!banner || end === null || !("countdown" in banner)) return NONE;
  if (running) return { seconds: Math.ceil((end - now) / 1000), ended: null };
  return { seconds: null, ended: banner.countdown?.ends ?? null };
};
